import { useEffect, useRef, useState } from "react";

import MetaDocAISummary from "../components/MetaDocAISummary";
import { useAIInsightDocument } from "../hooks/ai-insights/useAIInsightDocument";
import { useCodeEditor } from "../hooks/ai-insights/useCodeEditor";
import { useCodeMetadata } from "../hooks/ai-insights/useCodeMetadata";
import { useDocumentGroupLookup } from "../hooks/ai-insights/useDocumentGroupLookup";
import { useMedicationEditor } from "../hooks/ai-insights/useMedicationEditor";
import { useMedicationMetadata } from "../hooks/ai-insights/useMedicationMetadata";
import { useNonQualifiedItemsDocuments } from "../hooks/ai-insights/useNonQualifiedItemsDocuments";
import { getSummaryEmptyMessage } from "../utils/codeMetadataUtils";
import CodesPanel from "./ai-insights/CodesPanel";
import CurrentStatusPanel from "./ai-insights/CurrentStatusPanel";
import MedicationsPanel from "./ai-insights/MedicationsPanel";
import NonQualifiedItemsPanel from "./ai-insights/NonQualifiedItemsPanel";
import OtherSummaryPanel from "./ai-insights/OtherSummaryPanel";
import SummaryEmptyState from "./ai-insights/SummaryEmptyState";

import type { DocumentGroup } from "services/document-group-api";
import type { MetadocSummarySearchResult } from "services/metadoc-api";
import type { TabId } from "../metadoc.types";

type MetaDocAIInsightsPanelProps = {
  metadocId: number;
  tabId: TabId;
  activeDocumentGroup?: DocumentGroup;
  documentGroups?: DocumentGroup[];
  activeMedicationFilterQuery?: string;
  activeCodeFilterQuery?: string;
  activeSummarySearch?: {
    query: string;
    result: MetadocSummarySearchResult | null;
  };
};

type SummaryContext = {
  contextTitle: string;
  contextValue: string;
};

const EXECUTIVE_SUMMARY_TAB = "executive-summary";
const PER_RECORD_TAB = "per-record";
const CODES_TAB = "codes";
const MEDICATIONS_TAB = "medications";
const NOT_MEDICARE_COVERED_TAB = "not-medicare-covered";
const CURRENT_STATUS_TAB = "current-status";
const OTHER_SUMMARY_TAB = "other-summary";
const AI_INSIGHTS_LOADING_MESSAGE = "Loading AI insights...";

const matchesMedicationFilter = (
  medication: {
    name?: string;
    dosage?: string;
    frequency?: string;
    providerName?: string;
    date?: string;
  },
  query: string,
): boolean => {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) {
    return true;
  }

  return [
    medication.name,
    medication.dosage,
    medication.frequency,
    medication.providerName,
    medication.date,
  ].some((value) => value?.toLowerCase().includes(normalizedQuery));
};

const matchesCodeFilter = (
  code: {
    code?: string;
    codeType?: string;
    provider?: string;
    facility?: string;
    conditionType?: string;
    dateDetails?: Array<{
      date?: string;
      description?: string;
      notes?: string;
      reference?: string;
      pageNumber?: string;
    }>;
  },
  query: string,
): boolean => {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) {
    return true;
  }

  const detailMatches =
    code.dateDetails?.some((detail) =>
      [
        detail.date,
        detail.description,
        detail.notes,
        detail.reference,
        detail.pageNumber,
      ].some((value) => value?.toLowerCase().includes(normalizedQuery)),
    ) ?? false;

  if (detailMatches) {
    return true;
  }

  return [
    code.code,
    code.codeType,
    code.provider,
    code.facility,
    code.conditionType,
  ].some((value) => value?.toLowerCase().includes(normalizedQuery));
};

/**
 * builds the source badge label shown for per-record ai insight results.
 */
const getSourceContextLabel = (
  activeDocumentGroup?: DocumentGroup,
): SummaryContext | null => {
  if (!activeDocumentGroup) {
    return null;
  }

  const { start_pg: startPage, end_pg: endPage } = activeDocumentGroup;
  const pageRange =
    startPage === endPage
      ? `Page ${startPage}`
      : `Pages ${startPage}-${endPage}`;

  return {
    contextTitle: "Source",
    contextValue: pageRange,
  };
};

/*
renders ai tab summary content and ai-tab empty states
*/
const MetaDocAIInsightsPanel = ({
  metadocId,
  tabId,
  activeDocumentGroup,
  documentGroups = [],
  activeMedicationFilterQuery = "",
  activeCodeFilterQuery = "",
  activeSummarySearch,
}: MetaDocAIInsightsPanelProps) => {
  const isExecutiveSummaryTab = tabId === EXECUTIVE_SUMMARY_TAB;
  const isPerRecordSummaryTab = tabId === PER_RECORD_TAB;
  const isCodesTab = tabId === CODES_TAB;
  const isMedicationsTab = tabId === MEDICATIONS_TAB;
  const isNotMedicareCoveredTab = tabId === NOT_MEDICARE_COVERED_TAB;
  const isCurrentStatusTab = tabId === CURRENT_STATUS_TAB;
  const isOtherSummaryTab = tabId === OTHER_SUMMARY_TAB;
  const isPendingAIInsightsTab = isCodesTab || isMedicationsTab;
  const isSummaryTab = isExecutiveSummaryTab || isPerRecordSummaryTab;
  const isAIInsightsTab =
    isSummaryTab ||
    isPendingAIInsightsTab ||
    isNotMedicareCoveredTab ||
    isCurrentStatusTab ||
    isOtherSummaryTab;
  const shouldPrefetchMetadata = isAIInsightsTab;
  const sourceContextLabel = getSourceContextLabel(activeDocumentGroup);

  const {
    codes,
    isLoading: isCodeMetadataLoading,
    isFetching: isCodeMetadataFetching,
  } = useCodeMetadata({
    metadocId,
    activeDocumentGroupId: activeDocumentGroup?.id,
    shouldFetch: shouldPrefetchMetadata,
  });

  const {
    medications,
    isLoading: isMedicationMetadataLoading,
    isFetching: isMedicationMetadataFetching,
  } = useMedicationMetadata({
    metadocId,
    activeDocumentGroupId: activeDocumentGroup?.id,
    shouldFetch: shouldPrefetchMetadata,
  });

  const aiInsightDocument = useAIInsightDocument({
    metadocId,
    tabId,
    activeDocumentGroupId: activeDocumentGroup?.id,
    shouldFetch: shouldPrefetchMetadata,
  });

  const warningsAcceptedDocument = useAIInsightDocument({
    metadocId,
    tabId: "warnings-accepted",
    activeDocumentGroupId: activeDocumentGroup?.id,
    shouldFetch: isOtherSummaryTab,
  });

  const warningsDeniedDocument = useAIInsightDocument({
    metadocId,
    tabId: "warnings-denied",
    activeDocumentGroupId: activeDocumentGroup?.id,
    shouldFetch: isOtherSummaryTab,
  });

  const costMitigationsAcceptedDocument = useAIInsightDocument({
    metadocId,
    tabId: "cost-mitigations-accepted",
    activeDocumentGroupId: activeDocumentGroup?.id,
    shouldFetch: isOtherSummaryTab,
  });

  const costMitigationsDeniedDocument = useAIInsightDocument({
    metadocId,
    tabId: "cost-mitigations-denied",
    activeDocumentGroupId: activeDocumentGroup?.id,
    shouldFetch: isOtherSummaryTab,
  });

  const currentStatusAcceptedDocument = useAIInsightDocument({
    metadocId,
    tabId: "current-status-accepted",
    shouldFetch: isCurrentStatusTab,
  });

  const currentStatusDeniedDocument = useAIInsightDocument({
    metadocId,
    tabId: "current-status-denied",
    shouldFetch: isCurrentStatusTab,
  });

  const [isCurrentStatusCollapsed, setIsCurrentStatusCollapsed] =
    useState(false);
  const [isNonQualifiedItemsCollapsed, setIsNonQualifiedItemsCollapsed] =
    useState(false);

  const {
    items: nonQualifiedItems,
    isLoading: isNonQualifiedItemsLoading,
    isFetching: isNonQualifiedItemsFetching,
  } = useNonQualifiedItemsDocuments({
    metadocId,
    shouldFetch: isNotMedicareCoveredTab,
  });

  const { getReferenceContext } = useDocumentGroupLookup({
    documentGroups,
  });

  const {
    state: editorState,
    handleToggleCollapse,
    handleAddCode,
    handleCodePresetSave,
    handleConditionTypeChange,
    handleSectionChange,
    handleDateDetailFieldChange,
    handleDateDetailFieldSave,
    handleAssociationChange,
    handleAssociationSave,
    loadCodes,
  } = useCodeEditor({
    metadocId,
    initialCodes: [],
  });
  const {
    state: medicationEditorState,
    handleToggleCollapse: handleMedicationToggleCollapse,
    handleFieldChange: handleMedicationFieldChange,
    handleFieldSave: handleMedicationFieldSave,
    handleAddMedication,
    loadMedications,
  } = useMedicationEditor({
    metadocId,
    activeDocumentGroupId: activeDocumentGroup?.id,
    initialMedications: [],
  });

  const loadCodesRef = useRef(loadCodes);
  const loadMedicationsRef = useRef(loadMedications);

  useEffect(() => {
    loadCodesRef.current = loadCodes;
  }, [loadCodes]);

  useEffect(() => {
    loadMedicationsRef.current = loadMedications;
  }, [loadMedications]);

  useEffect(() => {
    loadCodesRef.current(codes);
  }, [codes]);

  useEffect(() => {
    loadMedicationsRef.current(medications);
  }, [medications]);

  useEffect(() => {
    loadCodesRef.current([]);
    loadMedicationsRef.current([]);
  }, [metadocId]);

  const isCodesLoading =
    isCodesTab &&
    codes.length === 0 &&
    (isCodeMetadataLoading || isCodeMetadataFetching);
  const isSummaryLoading =
    isSummaryTab &&
    !aiInsightDocument.content &&
    (aiInsightDocument.isLoading || aiInsightDocument.isFetching);
  const isCurrentStatusLoading =
    isCurrentStatusTab &&
    !currentStatusAcceptedDocument.content &&
    !currentStatusDeniedDocument.content &&
    (currentStatusAcceptedDocument.isLoading ||
      currentStatusAcceptedDocument.isFetching ||
      currentStatusDeniedDocument.isLoading ||
      currentStatusDeniedDocument.isFetching);
  const isOtherSummaryLoading =
    isOtherSummaryTab &&
    !warningsAcceptedDocument.content &&
    !warningsDeniedDocument.content &&
    !costMitigationsAcceptedDocument.content &&
    !costMitigationsDeniedDocument.content &&
    (warningsAcceptedDocument.isLoading ||
      warningsAcceptedDocument.isFetching ||
      warningsDeniedDocument.isLoading ||
      warningsDeniedDocument.isFetching ||
      costMitigationsAcceptedDocument.isLoading ||
      costMitigationsAcceptedDocument.isFetching ||
      costMitigationsDeniedDocument.isLoading ||
      costMitigationsDeniedDocument.isFetching);
  const isMedicationsLoading =
    isMedicationsTab &&
    medications.length === 0 &&
    (isMedicationMetadataLoading || isMedicationMetadataFetching);
  const isNonQualifiedItemsTabLoading =
    isNotMedicareCoveredTab &&
    nonQualifiedItems.length === 0 &&
    (isNonQualifiedItemsLoading || isNonQualifiedItemsFetching);
  const isActiveTabLoading =
    isCodesLoading ||
    isSummaryLoading ||
    isCurrentStatusLoading ||
    isMedicationsLoading ||
    isOtherSummaryLoading ||
    isNonQualifiedItemsTabLoading;

  if (!isAIInsightsTab) {
    return null;
  }

  const summaryValue = isExecutiveSummaryTab
    ? aiInsightDocument.editorValue || aiInsightDocument.content || ""
    : isPerRecordSummaryTab
      ? aiInsightDocument.editorValue || aiInsightDocument.content || ""
      : "";
  const hasSummaryDocument =
    isSummaryTab && aiInsightDocument.metadataId !== null;
  const canAddPerRecordSummary =
    isPerRecordSummaryTab &&
    typeof activeDocumentGroup?.id === "number" &&
    aiInsightDocument.canCreate;
  const handleSummaryBlur = () => {
    void aiInsightDocument.onBlurSave();
  };
  const handleSummaryChange = aiInsightDocument.onChange;
  const handleWarningsAcceptedChange = warningsAcceptedDocument.onChange;
  const handleWarningsAcceptedBlur = () => {
    void warningsAcceptedDocument.onBlurSave();
  };
  const handleWarningsAcceptedDateChange =
    warningsAcceptedDocument.onDateChange;
  const handleWarningsAcceptedDateBlur = () => {
    void warningsAcceptedDocument.onDateBlurSave();
  };
  const handleWarningsDeniedChange = warningsDeniedDocument.onChange;
  const handleWarningsDeniedBlur = () => {
    void warningsDeniedDocument.onBlurSave();
  };
  const handleWarningsDeniedDateChange = warningsDeniedDocument.onDateChange;
  const handleWarningsDeniedDateBlur = () => {
    void warningsDeniedDocument.onDateBlurSave();
  };
  const handleCostMitigationsAcceptedChange =
    costMitigationsAcceptedDocument.onChange;
  const handleCostMitigationsAcceptedBlur = () => {
    void costMitigationsAcceptedDocument.onBlurSave();
  };
  const handleCostMitigationsAcceptedDateChange =
    costMitigationsAcceptedDocument.onDateChange;
  const handleCostMitigationsAcceptedDateBlur = () => {
    void costMitigationsAcceptedDocument.onDateBlurSave();
  };
  const handleCostMitigationsDeniedChange =
    costMitigationsDeniedDocument.onChange;
  const handleCostMitigationsDeniedBlur = () => {
    void costMitigationsDeniedDocument.onBlurSave();
  };
  const handleCostMitigationsDeniedDateChange =
    costMitigationsDeniedDocument.onDateChange;
  const handleCostMitigationsDeniedDateBlur = () => {
    void costMitigationsDeniedDocument.onDateBlurSave();
  };
  const handleAcceptedChange = currentStatusAcceptedDocument.onChange;
  const handleAcceptedBlur = () => {
    void currentStatusAcceptedDocument.onBlurSave();
  };
  const handleAcceptedDateChange = currentStatusAcceptedDocument.onDateChange;
  const handleAcceptedDateBlur = () => {
    void currentStatusAcceptedDocument.onDateBlurSave();
  };
  const handleDeniedChange = currentStatusDeniedDocument.onChange;
  const handleDeniedBlur = () => {
    void currentStatusDeniedDocument.onBlurSave();
  };
  const handleDeniedDateChange = currentStatusDeniedDocument.onDateChange;
  const handleDeniedDateBlur = () => {
    void currentStatusDeniedDocument.onDateBlurSave();
  };
  const handleAddPerRecordSummary = () => {
    void aiInsightDocument.onCreate();
  };
  const summaryEmptyMessage = getSummaryEmptyMessage(
    tabId,
    activeDocumentGroup,
  );
  const activeSummarySearchTerm =
    activeSummarySearch?.query &&
    activeSummarySearch.result?.metadata_id === aiInsightDocument.metadataId
      ? activeSummarySearch.query
      : undefined;
  const filteredCodes = editorState.codes.filter((code) =>
    matchesCodeFilter(code, activeCodeFilterQuery),
  );
  const filteredMedications = medicationEditorState.medications.filter(
    (medication) =>
      matchesMedicationFilter(medication, activeMedicationFilterQuery),
  );

  return (
    <div className="metadoc-viewer-workspace__summary">
      {isActiveTabLoading && (
        <div className="metadoc-viewer-workspace__summary-empty">
          {AI_INSIGHTS_LOADING_MESSAGE}
        </div>
      )}
      {isCodesTab && !isActiveTabLoading && (
        <CodesPanel
          codes={filteredCodes}
          emptyMessage={summaryEmptyMessage}
          sourceContext={sourceContextLabel ?? undefined}
          isCollapsed={editorState.isCollapsed}
          isAddingCode={editorState.isAddingCode}
          onToggleCollapse={handleToggleCollapse}
          onAddCode={handleAddCode}
          onCodePresetSave={handleCodePresetSave}
          onConditionTypeChange={handleConditionTypeChange}
          onSectionChange={handleSectionChange}
          onFieldChange={handleDateDetailFieldChange}
          onFieldSave={handleDateDetailFieldSave}
          onAssociationChange={handleAssociationChange}
          onAssociationSave={handleAssociationSave}
          getReferenceContext={getReferenceContext}
        />
      )}
      {isMedicationsTab &&
        !isActiveTabLoading &&
        typeof activeDocumentGroup?.id === "number" && (
          <MedicationsPanel
            medications={filteredMedications}
            emptyMessage={summaryEmptyMessage}
            sourceContext={sourceContextLabel ?? undefined}
            isCollapsed={medicationEditorState.isCollapsed}
            isAddingMedication={medicationEditorState.isSaving}
            canAddMedication={typeof activeDocumentGroup?.id === "number"}
            onToggleCollapse={handleMedicationToggleCollapse}
            onAddMedication={handleAddMedication}
            onFieldChange={handleMedicationFieldChange}
            onFieldSave={handleMedicationFieldSave}
          />
        )}
      {!isActiveTabLoading &&
        (isExecutiveSummaryTab || isPerRecordSummaryTab) &&
        (summaryValue || hasSummaryDocument) && (
          <MetaDocAISummary
            summary={summaryValue}
            tabId={tabId}
            onChange={handleSummaryChange}
            onBlur={handleSummaryBlur}
            isEditable={true}
            searchTerm={activeSummarySearchTerm}
            summaryContext={
              isPerRecordSummaryTab
                ? (sourceContextLabel ?? undefined)
                : undefined
            }
          />
        )}
      {!isActiveTabLoading && isCurrentStatusTab && (
        <CurrentStatusPanel
          accepted={
            currentStatusAcceptedDocument.editorValue ||
            currentStatusAcceptedDocument.content ||
            ""
          }
          denied={
            currentStatusDeniedDocument.editorValue ||
            currentStatusDeniedDocument.content ||
            ""
          }
          acceptedDate={currentStatusAcceptedDocument.dateValue}
          deniedDate={currentStatusDeniedDocument.dateValue}
          onAcceptedChange={handleAcceptedChange}
          onAcceptedBlur={handleAcceptedBlur}
          onAcceptedDateChange={handleAcceptedDateChange}
          onAcceptedDateBlur={handleAcceptedDateBlur}
          onDeniedChange={handleDeniedChange}
          onDeniedBlur={handleDeniedBlur}
          onDeniedDateChange={handleDeniedDateChange}
          onDeniedDateBlur={handleDeniedDateBlur}
          isEditable={true}
          isCollapsed={isCurrentStatusCollapsed}
          onToggleCollapse={() => setIsCurrentStatusCollapsed((v) => !v)}
        />
      )}
      {!isActiveTabLoading && isOtherSummaryTab && (
        <OtherSummaryPanel
          acceptedWarnings={{
            value:
              warningsAcceptedDocument.editorValue ||
              warningsAcceptedDocument.content ||
              "",
            date: warningsAcceptedDocument.dateValue,
            onChange: handleWarningsAcceptedChange,
            onBlur: handleWarningsAcceptedBlur,
            onDateChange: handleWarningsAcceptedDateChange,
            onDateBlur: handleWarningsAcceptedDateBlur,
          }}
          deniedWarnings={{
            value:
              warningsDeniedDocument.editorValue ||
              warningsDeniedDocument.content ||
              "",
            date: warningsDeniedDocument.dateValue,
            onChange: handleWarningsDeniedChange,
            onBlur: handleWarningsDeniedBlur,
            onDateChange: handleWarningsDeniedDateChange,
            onDateBlur: handleWarningsDeniedDateBlur,
          }}
          acceptedCostMitigations={{
            value:
              costMitigationsAcceptedDocument.editorValue ||
              costMitigationsAcceptedDocument.content ||
              "",
            date: costMitigationsAcceptedDocument.dateValue,
            onChange: handleCostMitigationsAcceptedChange,
            onBlur: handleCostMitigationsAcceptedBlur,
            onDateChange: handleCostMitigationsAcceptedDateChange,
            onDateBlur: handleCostMitigationsAcceptedDateBlur,
          }}
          deniedCostMitigations={{
            value:
              costMitigationsDeniedDocument.editorValue ||
              costMitigationsDeniedDocument.content ||
              "",
            date: costMitigationsDeniedDocument.dateValue,
            onChange: handleCostMitigationsDeniedChange,
            onBlur: handleCostMitigationsDeniedBlur,
            onDateChange: handleCostMitigationsDeniedDateChange,
            onDateBlur: handleCostMitigationsDeniedDateBlur,
          }}
          emptyMessage={summaryEmptyMessage}
          sourceContext={
            typeof activeDocumentGroup?.id === "number"
              ? (sourceContextLabel ?? undefined)
              : undefined
          }
          isEditable={true}
        />
      )}
      {!isActiveTabLoading && isNotMedicareCoveredTab && (
        <NonQualifiedItemsPanel
          items={nonQualifiedItems}
          emptyMessage={summaryEmptyMessage}
          isEditable={true}
          isCollapsed={isNonQualifiedItemsCollapsed}
          onToggleCollapse={() => setIsNonQualifiedItemsCollapsed((v) => !v)}
        />
      )}
      {!isActiveTabLoading &&
        (isExecutiveSummaryTab || isPerRecordSummaryTab) &&
        !summaryValue &&
        !hasSummaryDocument &&
        (isPerRecordSummaryTab ? (
          <div className="metadoc-ai-summary metadoc-ai-summary--visible">
            <div className="metadoc-ai-summary__header">
              <div className="metadoc-ai-summary__badge">
                <span className="material-icons">auto_awesome</span>
                <span className="metadoc-ai-summary__badge-text">
                  AI Generated Summary
                </span>
              </div>
              <div className="metadoc-ai-summary__header-actions">
                {canAddPerRecordSummary && (
                  <button
                    type="button"
                    className="metadoc-ai-summary__context"
                    onClick={handleAddPerRecordSummary}
                    disabled={aiInsightDocument.isCreating}
                    title="Add per record summary"
                  >
                    <span className="metadoc-ai-summary__context-title">
                      Summary
                    </span>
                    <span className="metadoc-ai-summary__context-value">
                      Add+
                    </span>
                  </button>
                )}
                {sourceContextLabel && (
                  <div className="metadoc-ai-summary__context">
                    <span className="metadoc-ai-summary__context-title">
                      {sourceContextLabel.contextTitle}
                    </span>
                    <span className="metadoc-ai-summary__context-value">
                      {sourceContextLabel.contextValue}
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div className="metadoc-viewer-workspace__summary-empty">
              <span className="metadoc-viewer-workspace__summary-empty-message">
                {summaryEmptyMessage}
              </span>
            </div>
          </div>
        ) : (
          <div className="metadoc-viewer-workspace__summary-empty">
            <span className="metadoc-viewer-workspace__summary-empty-message">
              {summaryEmptyMessage}
            </span>
          </div>
        ))}
      {!isActiveTabLoading &&
        isMedicationsTab &&
        typeof activeDocumentGroup?.id !== "number" && (
          <SummaryEmptyState message={summaryEmptyMessage} />
        )}
    </div>
  );
};

export default MetaDocAIInsightsPanel;
